import type { PedigreeType } from '@application/types';
import { pedigreeSelectOptions } from '@application/utils';
import { ParentTypeEnum } from '@gql/graphql';
import { Button, InputField, InputLabel } from '@presentation/components';
import { InputDateField } from '@presentation/components/InputDateField';
import { InputRadioField } from '@presentation/components/InputRadioField';
import { InputSelectField } from '@presentation/components/InputSelectField';
import { InputWrapper } from '@presentation/components/InputWrapper';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { FaPlus } from 'react-icons/fa';
import * as Styled from '../styled';
import type { MtLitterPageFormValues, MtLitterPageProps } from '../types';

export const FormStepGeneral: FC<MtLitterPageProps> = (props) => {
  const { mode, options, onAddParentClick, confirmBorn, canEditSpecificFields, showLitterCode } =
    props;
  const { values, setFieldValue } = useFormikContext<MtLitterPageFormValues>();
  const { translate } = useTranslation();

  return (
    <>
      {mode !== 'new' && !values.born && (
        <InputWrapper>
          <Button
            size="small"
            color="blue"
            // onClick={() => setFieldValue('born', true)}
            onClick={async () => {
              const born = await confirmBorn?.();
              if (!born) return;
              setFieldValue('born', true);
            }}
            // disabled={mode === 'view'}
          >
            {translate('common.buttons.litterBorned')}
          </Button>
        </InputWrapper>
      )}
      <InputWrapper>
        {[ParentTypeEnum.MOTHER, ParentTypeEnum.FATHER].map((parentType, index) => {
          const disabled = values[parentType] != null;
          return (
            <InputWrapper split key={index}>
              <InputLabel required>{translate(`enum.parentType.${parentType}`)}</InputLabel>
              <Styled.ParentInputWrapper>
                <InputSelectField
                  disabled={mode === 'view' || canEditSpecificFields === false}
                  clearButton={{
                    showOnNotEmpty: true,
                  }}
                  useSearch={false}
                  placeholder={translate('common.inputs.choose.placeholder')}
                  name={parentType}
                  options={options.animals[parentType]}
                />
                <Styled.ParentAddButton
                  onClick={() => !disabled && onAddParentClick?.(parentType, values)}
                  disabled={disabled}
                >
                  <FaPlus />
                </Styled.ParentAddButton>
              </Styled.ParentInputWrapper>
            </InputWrapper>
          );
        })}

        <InputWrapper inline width="33%" last>
          <InputLabel required>{translate('common.inputs.breed.label')}</InputLabel>
          <InputSelectField<number>
            disabled={mode === 'view'}
            name="breedId"
            options={options.breeds}
          />
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>
          {translate(
            values.born ? 'common.inputs.birthday.label' : 'common.inputs.birthdayExpected.label',
          )}
        </InputLabel>
        <InputDateField
          disabled={mode === 'view' || canEditSpecificFields === false}
          name="birthday"
          disableFutureDates={values.born}
          disablePastDates={!values.born}
        />
      </InputWrapper>

      {values.born && (
        <InputWrapper split last>
          <InputLabel required>{translate('common.inputs.availability.label')}</InputLabel>
          <InputDateField
            disabled={mode === 'view' || canEditSpecificFields === false}
            name="availabilityDate"
          />
        </InputWrapper>
      )}

      <div>
        {values.fixedPrice ? (
          <InputWrapper inline width="50%">
            <InputLabel required>{translate('common.inputs.priceInput.label')}</InputLabel>
            <InputField disabled={mode === 'view'} name="price" type="number" inputMode="numeric" />
          </InputWrapper>
        ) : (
          <>
            <InputWrapper inline width="25%">
              <InputLabel required>{translate('common.inputs.minPrice.label')}</InputLabel>
              <InputField
                disabled={mode === 'view'}
                name="priceFrom"
                type="number"
                inputMode="numeric"
              />
            </InputWrapper>
            <InputWrapper inline width="25%">
              <InputLabel required>{translate('common.inputs.maxPrice.label')}</InputLabel>
              <InputField
                disabled={mode === 'view'}
                name="priceTo"
                type="number"
                inputMode="numeric"
              />
            </InputWrapper>
          </>
        )}
        <InputWrapper inline width="50%" last>
          <InputLabel required>{translate('common.inputs.fixedPrice.label')}</InputLabel>
          <InputRadioField<boolean>
            grow
            disabled={mode === 'view'}
            name="fixedPrice"
            options={[
              {
                value: false,
                label: translate('common.options.fixedPrice.false'),
              },
              {
                value: true,
                label: translate('common.options.fixedPrice.true'),
              },
            ]}
          />
        </InputWrapper>
      </div>

      <InputWrapper>
        <InputLabel required>{translate('common.inputs.pedigree.label')}</InputLabel>
        <InputSelectField<PedigreeType>
          disabled={mode === 'view'}
          useSearch={false}
          name="pedigree"
          options={pedigreeSelectOptions(translate, {
            showText: true,
            showIcons: false,
            value: values.pedigree,
          })}
        />
      </InputWrapper>

      {values.pedigree != null && values.pedigree !== 'FCI' && (
        <InputWrapper>
          <InputLabel>{translate('common.inputs.otherPedigree.label')}</InputLabel>
          <InputField
            disabled={mode === 'view'}
            name="pedigree"
            placeholder={translate('common.inputs.pedigreeNumber.placeholder')}
          />
        </InputWrapper>
      )}

      {showLitterCode && (
        <>
          <InputWrapper>
            <InputLabel required>{translate('common.inputs.litterCode.explanation')}</InputLabel>
            <InputRadioField<boolean>
              grow
              disabled={mode === 'view'}
              name="hasLitterCode"
              onSelect={() => setFieldValue('litterCode', '')}
              options={[
                {
                  value: false,
                  label: translate('common.buttons.no'),
                },
                {
                  value: true,
                  label: translate('common.buttons.yes'),
                },
              ]}
            />
          </InputWrapper>
          {values.hasLitterCode && (
            <InputWrapper>
              <InputLabel required>{translate('common.inputs.litterCode.label')}</InputLabel>
              <InputField
                disabled={mode === 'view'}
                name="litterCode"
                placeholder={translate('common.inputs.litterCode.placeholder')}
              />
            </InputWrapper>
          )}
        </>
      )}
    </>
  );
};
